import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { BRAND, SERVICES, CREDENTIALS } from "@/lib/content";
import { AREAS } from "@/lib/areas";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-brand-navy text-brand-cream">
      <div className="container-narrow py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <img
                src={BRAND.logoUrl}
                alt="Declan Bishop Property Consultant"
                className="h-20 w-20 md:h-24 md:w-24 object-contain bg-brand-cream/95 p-1.5"
              />
              <div className="leading-tight">
                <div className="font-serif text-2xl">Declan Bishop</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
                  Property Consultant
                </div>
              </div>
            </div>
            <p className="mt-8 text-brand-cream/70 text-sm leading-relaxed max-w-md">
              Independent, accredited property surveying and reporting throughout Lincolnshire.
              Plain-English advice for homeowners, landlords, buyers and estate agents.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[11px] tracking-[0.22em] uppercase text-brand-champagne font-semibold">
              {CREDENTIALS.map((c) => (
                <span key={c.code} data-testid={`footer-credential-${c.code}`}>{c.code}</span>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold mb-6">
              Services
            </div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services#${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sm text-brand-cream/80 hover:text-brand-champagne transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas */}
          <div className="md:col-span-2">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold mb-6">
              Areas
            </div>
            <ul className="space-y-3">
              {AREAS.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/areas/${a.slug}`}
                    data-testid={`footer-area-${a.slug}`}
                    className="text-sm text-brand-cream/80 hover:text-brand-champagne transition-colors"
                  >
                    {a.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/areas"
                  data-testid="footer-all-areas"
                  className="text-sm text-brand-champagne hover:text-brand-cream transition-colors"
                >
                  All areas →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold mb-6">
              Contact
            </div>
            <ul className="space-y-4 text-sm text-brand-cream/85">
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-brand-champagne" />
                <a href={`tel:${BRAND.phone.replace(/\s+/g, "")}`} data-testid="footer-phone" className="hover:text-brand-champagne">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-brand-champagne" />
                <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="hover:text-brand-champagne">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-brand-champagne" />
                <span data-testid="footer-area">
                  {BRAND.addressLines.join(" · ")}
                  <br />
                  <span className="text-brand-cream/55 text-xs tracking-[0.18em] uppercase">
                    {BRAND.serviceAreaLabel}
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-brand-cream/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-xs text-brand-cream/55">
          <div>© {year} Declan Bishop Property Consultant. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/home-surveys" data-testid="footer-hs-link" className="hover:text-brand-champagne">
              RICS Home Surveys
            </Link>
            <Link to="/epc" data-testid="footer-epc-link" className="hover:text-brand-champagne">
              EPCs from £85
            </Link>
            <Link to="/condition-surveys" data-testid="footer-cs-link" className="hover:text-brand-champagne">
              Condition Surveys
            </Link>
            <Link to="/blog" data-testid="footer-blog-link" className="hover:text-brand-champagne">
              Blog
            </Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-brand-champagne">
              Privacy Policy
            </Link>
            <span className="tracking-[0.22em] uppercase">{BRAND.hours}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
